import { COMPANY, DPO } from "./content/company";
import type { LegalBlock, LegalDocument, RichText } from "./content/types";
import { formatLegalDate } from "./utils";

/**
 * Versão Markdown de um documento jurídico, servida em `/<rota>.md` para
 * leitura por LLMs — o mesmo papel do `/blog/<slug>.md` dos posts. Sai do
 * mesmo `LegalDocument` que a página renderiza: texto da tela e do `.md`
 * nunca divergem.
 */

// O `RichText` já é Markdown (`**negrito**`, `[rótulo](href)`); só os links
// relativos precisam virar absolutos, porque o `.md` é lido fora do site.
const absolutize = (text: RichText, pageUrl: string, origin: string) =>
  text.replace(/\]\(([^)]+)\)/g, (_, href: string) => {
    if (href.startsWith("#")) return `](${pageUrl}${href})`;
    if (href.startsWith("/")) return `](${origin}${href})`;
    return `](${href})`;
  });

const cell = (text: string) => text.replace(/\|/g, "\\|").replace(/\n/g, " ");

const blockToMarkdown = (
  block: LegalBlock,
  rich: (text: RichText) => string
): string => {
  switch (block.type) {
    case "paragraph":
      return rich(block.text);
    case "subheading":
      return `### ${block.text}`;
    case "list":
      return block.items
        .map((item, index) =>
          block.ordered ? `${index + 1}. ${rich(item)}` : `- ${rich(item)}`
        )
        .join("\n");
    case "table": {
      const header = `| ${block.columns.map(column => cell(column.header)).join(" | ")} |`;
      const divider = `| ${block.columns.map(() => "---").join(" | ")} |`;
      const rows = block.rows.map(
        row =>
          `| ${block.columns.map(column => cell(rich(row[column.key] ?? "—"))).join(" | ")} |`
      );
      return [`**${block.caption}**`, "", header, divider, ...rows].join("\n");
    }
    case "timeline":
      return [
        `**${block.caption}**`,
        "",
        ...block.items.map(item => `- **${item.time}:** ${rich(item.text)}`),
      ].join("\n");
    case "faq":
      return block.items
        .map(item => `### ${item.question}\n\n${rich(item.answer)}`)
        .join("\n\n");
    case "callout":
      return `> **${block.title}:** ${rich(block.text)}`;
    case "action":
      return `[${block.label}](${block.href})`;
  }
};

export const documentToMarkdown = (
  document: LegalDocument,
  origin: string
): string => {
  const pageUrl = `${origin}${document.path}`;
  const rich = (text: RichText) => absolutize(text, pageUrl, origin);

  const parts = [
    `# ${document.title}`,
    `> ${rich(document.intro)}`,
    `Atualizada em ${formatLegalDate(document.updatedAt)} · Versão ${document.version} · Página: ${pageUrl}`,
    "## O essencial",
    document.highlights
      .map(highlight => `- **${highlight.title}:** ${rich(highlight.text)}`)
      .join("\n"),
    ...document.sections.flatMap((section, index) => [
      `## ${index + 1}. ${section.title}`,
      `**Em poucas palavras:** ${rich(section.summary)}`,
      ...section.blocks.map(block => blockToMarkdown(block, rich)),
    ]),
    "## Encarregado de dados",
    `${DPO.name} — ${DPO.email}. ${COMPANY.legalName}, CNPJ ${COMPANY.cnpj}, ${COMPANY.address}.`,
    "## Histórico de alterações",
    document.changelog
      .map(
        entry =>
          `- **Versão ${entry.version}** (${formatLegalDate(entry.date)}): ${entry.changes.join(" ")}`
      )
      .join("\n"),
  ];

  return `${parts.join("\n\n")}\n`;
};

/** URL do `.md` de uma página jurídica. */
export const markdownPath = (path: string) => `${path}.md`;
